import React, { createContext, useState, useContext, useEffect, ReactNode, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosInstance';
import config from '../utils/config';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import { applyPalette, readRememberedPalette, rememberPalette, resolvePalette, PALETTE_STORAGE_KEY, type PaletteId } from '../palette';

interface PaletteSetting {
  palette: string;
  isDefault: boolean;
}

const paletteUrl = `${config.devicesApiUrl}/system/settings/palette`;

interface AuthContextType {
  id: number;
  email: string | null;
  isLoggedIn: boolean;
  authChecked: boolean;
  permissions: string[];
  scopes: string[];
  hasPermission: (name: string) => boolean;
  login: (token: string) => void;
  logout: () => void;
  getToken: () => string | null;
  savedPalette: PaletteId;
  draftPalette: PaletteId | null;
  paletteLoading: boolean;
  paletteError: string | null;
  previewPalette: (palette: PaletteId) => void;
  cancelPalettePreview: () => void;
  savePalette: (palette: PaletteId) => Promise<void>;
}

interface JWTPayload {
  userId: number;
  email?: string;
  permissions?: string[];
  scopes?: string[];
  iat?: number;
  exp?: number;
}

interface AuthProviderProps {
  children: ReactNode;
}

const decodeJWT = (token: string): JWTPayload | null => {
  try {
    const base64Payload = token.split('.')[1];
    const base64 = base64Payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

const isTokenExpired = (payload: JWTPayload): boolean => {
  if (!payload.exp) return false;
  // Add a 30-second buffer to handle timing differences
  return payload.exp * 1000 <= Date.now() + 30000;
};

const permissionsFromPayload = (payload: JWTPayload): string[] =>
  Array.isArray(payload.permissions) ? payload.permissions : [];

const scopesFromPayload = (payload: JWTPayload): string[] =>
  Array.isArray(payload.scopes) ? payload.scopes : [];

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [authChecked, setAuthChecked] = useState<boolean>(false);
  const [id, setId] = useState<number>(-1);
  const [email, setEmail] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [scopes, setScopes] = useState<string[]>([]);
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const [savedPalette, setSavedPalette] = useState<PaletteId>(readRememberedPalette);
  const [draftPalette, setDraftPalette] = useState<PaletteId | null>(null);
  const [paletteLoading, setPaletteLoading] = useState(false);
  const [paletteError, setPaletteError] = useState<string | null>(null);
  // Token whose payload is currently reflected in state. Lets the periodic
  // check skip setState when nothing changed — decodeJWT returns fresh
  // arrays every call, which would otherwise re-render the whole tree
  // every minute (and e.g. reset Plotly chart zoom).
  const lastTokenRef = useRef<string | null>(null);
  const sessionUserRef = useRef<number | null>(null);
  const committedRef = useRef(savedPalette);
  const draftRef = useRef<PaletteId | null>(null);
  const draftRevisionRef = useRef(0);
  const committedRevisionRef = useRef(0);

  const beginSession = useCallback((userId: number) => {
    if (sessionUserRef.current === userId) return;
    sessionUserRef.current = userId;
    setSessionEpoch((epoch) => epoch + 1);
  }, []);

  const endSession = useCallback(() => {
    sessionUserRef.current = null;
    setSessionEpoch(0);
  }, []);

  const commitPalette = useCallback((value: unknown, persist: boolean) => {
    const palette = resolvePalette(value);
    committedRef.current = palette;
    setSavedPalette(palette);
    if (persist) rememberPalette(palette);
    if (draftRef.current === null) applyPalette(palette);
  }, []);

  const previewPalette = useCallback((palette: PaletteId) => {
    const draft = palette === committedRef.current ? null : palette;
    draftRevisionRef.current += 1;
    draftRef.current = draft;
    setDraftPalette(draft);
    applyPalette(draft ?? committedRef.current);
  }, []);

  const cancelPalettePreview = useCallback(() => {
    draftRevisionRef.current += 1;
    draftRef.current = null;
    setDraftPalette(null);
    applyPalette(committedRef.current);
  }, []);

  const checkAndUpdateToken = useCallback(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = decodeJWT(token);
      if (payload && !isTokenExpired(payload)) {
        if (token !== lastTokenRef.current) {
          lastTokenRef.current = token;
          beginSession(payload.userId);
          setId(payload.userId);
          setEmail(payload.email ?? null);
          setIsLoggedIn(true);
          setPermissions(permissionsFromPayload(payload));
          setScopes(scopesFromPayload(payload));
        }
      } else {
        // Token is invalid or expired
        localStorage.removeItem('token');
        lastTokenRef.current = null;
        endSession();
        setIsLoggedIn(false);
        setId(-1);
        setEmail(null);
        setPermissions([]);
        setScopes([]);
      }
    }
    setAuthChecked(true);
  }, [beginSession, endSession]);

  // Initial token check
  useEffect(() => {
    checkAndUpdateToken();
  }, [checkAndUpdateToken]);

  // Set up periodic token check
  useEffect(() => {
    const interval = setInterval(checkAndUpdateToken, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [checkAndUpdateToken]);

  useEffect(() => {
    applyPalette(committedRef.current);
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== PALETTE_STORAGE_KEY) return;
      committedRevisionRef.current += 1;
      commitPalette(event.newValue, false);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [commitPalette]);

  useEffect(() => {
    if (sessionEpoch === 0) return;
    let active = true;
    const revision = committedRevisionRef.current;
    setPaletteLoading(true);
    setPaletteError(null);
    axiosInstance.get<PaletteSetting>(paletteUrl).then(({ data }) => {
      if (!active || revision !== committedRevisionRef.current) return;
      commitPalette(data.palette, true);
    }).catch((error: unknown) => {
      if (!active) return;
      const reason = extractErrorMessage(error, 'Palette could not be loaded');
      console.error('Error fetching system colour palette:', reason, error);
      setPaletteError(`Failed to load colour palette: ${reason}`);
    }).finally(() => {
      if (active) setPaletteLoading(false);
    });
    return () => { active = false; };
  }, [sessionEpoch, commitPalette]);

  const savePalette = useCallback(async (palette: PaletteId) => {
    const startedDraftRevision = draftRevisionRef.current;
    const startedCommittedRevision = committedRevisionRef.current;
    const clearStartingDraft = () => {
      if (draftRevisionRef.current !== startedDraftRevision) return;
      cancelPalettePreview();
    };

    try {
      const { data } = await axiosInstance.put<PaletteSetting>(paletteUrl, { palette });
      let saved = resolvePalette(data.palette);
      if (committedRevisionRef.current !== startedCommittedRevision) {
        // A newer storage commit can arrive during the read, so use a response
        // only when it matches the latest revision observed by this tab.
        while (true) {
          const revision = committedRevisionRef.current;
          let currentPalette: PaletteId;
          try {
            const current = await axiosInstance.get<PaletteSetting>(paletteUrl);
            currentPalette = resolvePalette(current.data.palette);
          } catch (error) {
            const reason = extractErrorMessage(error, 'Palette could not be loaded');
            setPaletteError(`Could not confirm the saved colour palette: ${reason}`);
            throw error;
          }
          if (revision !== committedRevisionRef.current) continue;
          saved = currentPalette;
          break;
        }
      }
      clearStartingDraft();
      committedRevisionRef.current += 1;
      commitPalette(saved, true);
      setPaletteError(null);
    } catch (error) {
      clearStartingDraft();
      throw error;
    }
  }, [cancelPalettePreview, commitPalette]);

  const login = (token: string) => {
    const payload = decodeJWT(token);
    if (payload && !isTokenExpired(payload)) {
      localStorage.setItem('token', token);
      lastTokenRef.current = token;
      beginSession(payload.userId);
      setId(payload.userId);
      setEmail(payload.email ?? null);
      setIsLoggedIn(true);
      setPermissions(permissionsFromPayload(payload));
      setScopes(scopesFromPayload(payload));
    } else {
      console.error('Invalid or expired token provided');
      logout();
    }
  };

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    lastTokenRef.current = null;
    endSession();
    cancelPalettePreview();
    setPaletteError(null);
    setIsLoggedIn(false);
    setId(-1);
    setEmail(null);
    setPermissions([]);
    setScopes([]);
  }, [cancelPalettePreview, endSession]);

  const getToken = useCallback((): string | null => {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = decodeJWT(token);
      if (payload && !isTokenExpired(payload)) {
        return token;
      }
      // Token is expired or invalid
      logout();
    }
    return null;
  }, [logout]);

  const hasPermission = useCallback(
    (name: string): boolean => permissions.includes(name),
    [permissions]
  );

  return (
    <AuthContext.Provider value={{
      id,
      email,
      isLoggedIn,
      authChecked,
      permissions,
      scopes,
      hasPermission,
      login,
      logout,
      getToken,
      savedPalette,
      draftPalette,
      paletteLoading,
      paletteError,
      previewPalette,
      cancelPalettePreview,
      savePalette,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
};

export default AuthProvider;
